import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import {lastValueFrom, Observable, throwError} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ApiResponse } from '../interfaces/api-response.interface';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class BaseService {
  protected apiUrl = environment.apiUrl;
  protected headers = new HttpHeaders().set('Content-Type', 'application/json');

  constructor(protected http: HttpClient, protected toastr: ToastrService) {}

  protected getBlob<T>(endpoint: string): Promise<HttpResponse<T>> {
    return lastValueFrom(
      this.http.get<T>(`${this.apiUrl}/${endpoint}`, {
        observe: 'response',
        responseType: 'blob' as 'json'
      })
    );
  }

  protected get<T>(endpoint: string, params?: any): Promise<T> {
    const isLogin = endpoint === 'acesso/logar';

    const options = {
      headers: this.headers,
      params: new HttpParams({ fromObject: params })
    };

    return lastValueFrom(this.http.get<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, options)
      .pipe(
        map(response => this.handleResponse<T>(response)),
        catchError(error => this.handleError(error, isLogin))
      ));
  }

  protected getExternal<T>(endpoint: string, params?: any): Promise<T> {
    const isLogin = endpoint === 'acesso/logar';

    const options = {
      headers: this.headers,
      params: new HttpParams({ fromObject: params })
    };

    return lastValueFrom(this.http.get<T>(`${endpoint}`, options)
      .pipe(
        map(response => this.handleResponseExternal<T>(response)),
        catchError(error => this.handleError(error, isLogin))
      ));
  }

  protected post<T>(endpoint: string, data: any): Promise<T> {
    const isLogin = endpoint === 'acesso/logar';

    return lastValueFrom(this.http.post<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, { headers: this.headers })
      .pipe(
        map(response => this.handleResponse<T>(response)),
        catchError(error => this.handleError(error, isLogin))
      ));
  }

  protected postExternal<T>(endpoint: string, data: any): Promise<T> {
    const isLogin = endpoint === 'acesso/logar';

    return lastValueFrom(this.http.post<T>(`${endpoint}`, data, { headers: this.headers })
      .pipe(
        map(response => this.handleResponseExternal<T>(response)),
        catchError(error => this.handleError(error, isLogin))
      ));
  }

  protected put<T>(endpoint: string, data: any): Promise<T> {
    return lastValueFrom(this.http.put<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, { headers: this.headers })
      .pipe(
        map(response => this.handleResponse<T>(response)),
        catchError(error => this.handleError(error))
      ));
  }

  protected delete<T>(endpoint: string): Promise<T> {
    return lastValueFrom(this.http.delete<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, { headers: this.headers })
      .pipe(
        map(response => this.handleResponse<T>(response)),
        catchError(error => this.handleError(error))
      ));
  }

  private handleResponse<T>(response: ApiResponse<T>): T {
    if (!response.sucesso || response.codigoRetorno !== 200) {
      throw new Error(response.mensagens?.join('\n') || 'Erro na requisição');
    }
    return response.dados as T;
  }

  private handleResponseExternal<T>(response: T): T {
    try{
      return response as T;
    }
    catch (error) {
      throw new Error('Erro na conversão da resposta');
    }
  }

  private handleError(error: any, isLogin: boolean = false): Observable<never> {
    let errorMessage: string;
    let errorDesconhecido = false;

     const isSignalRRequest = this.isSignalRRequest(error);

    if (!error?.error) {
      errorMessage = error?.message || 'Erro desconhecido.';
    } else if (error.error instanceof ErrorEvent) {
      errorMessage = error.message;
    } else if(error.error.codigoRetorno && (error.status === 403 || error.error.codigoRetorno === 403)) {
      errorMessage = 'Você não tem permissão para acessar este recurso.';
    } else if(error.error && error.error.mensagens && error.error.mensagens.length > 0) {
      errorMessage = error.error.mensagens.join('\n');
    } else {
      errorDesconhecido = true;
      errorMessage = 'Erro desconhecido.';
    }

    if(isLogin) {
      errorMessage = 'Não foi possível efetuar o Login.\n\nMotivo: ' + errorMessage;
    }

    if (!isSignalRRequest) {
      if (errorDesconhecido) {
        this.toastr.error('Não foi possível concluir a operação.');
      } else {
        this.toastr.error(errorMessage);
      }
    }

    console.error('Erro:', error);

    return throwError(() => new Error(errorMessage));
  }

  private isSignalRRequest(error: any): boolean {
    const url = error?.url || '';
    return url.includes('/signalr') || 
          url.includes('/negotiate') || 
          url.includes('/hubs/') ||
          url.toLowerCase().includes('notification');
  }
}
